import {
    sendDeleteRequest,
    sendPatchRequest,
    sendPostRequest,
} from '../../utils';
import RowContextMenuButtonColumn from '../formatters/RowContextMenuButtonColumn';
import { addAlert } from '../../alerts';
import { initializeTable } from '../tables';

function initializeMailRecipientsTable() {
    const rootElement = document.querySelector('#mailRecipientsTable');
    if (!rootElement) return;

    const API_URL = '/api/notifications/mail_recipients/';
    const saveButton = document.getElementById('save-recipient-changes-button');
    const pillsContainerSelector = '.active-filters-container';

    const strictEmailValidator = (cell, value) => {
        if (!value) return true;
        return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
            String(value),
        );
    };
    const setButtonsEnabled = (isEnabled) => {
        saveButton.disabled = !isEnabled;
    };
    const textWrapperFormatter = function (cell, formatterParams) {
        const value = cell.getValue();
        return `<span class="cell-text-wrapper">${value || ''}</span>`;
    };

    const checkTableState = (activeEdit = null) => {
        const newRows = table.getRows().filter((row) => !row.getData().id);
        const editedCells = table.getEditedCells();

        const dirtyRows = new Set([
            ...newRows,
            ...editedCells.map((cell) => cell.getRow()),
        ]);
        if (activeEdit) {
            dirtyRows.add(activeEdit.cell.getRow());
        }

        if (dirtyRows.size === 0) {
            saveButton.disabled = true;
            return;
        }

        let allDirtyRowsArePopulated = true;
        dirtyRows.forEach((row) => {
            let data = row.getData();

            if (activeEdit && row === activeEdit.cell.getRow()) {
                const field = activeEdit.cell.getField();
                data = { ...data, [field]: activeEdit.liveValue };
            }

            if (!data.name || !data.email) {
                allDirtyRowsArePopulated = false;
            }
        });

        saveButton.disabled = !allDirtyRowsArePopulated;
    };

    const liveInputEditor = function (cell, onRendered, success, cancel) {
        const input = document.createElement('input');
        input.setAttribute('type', 'text');
        const initialValue = cell.getValue();
        input.value =
            initialValue === null || initialValue === undefined
                ? ''
                : initialValue;

        input.addEventListener('input', function (e) {
            checkTableState({ cell: cell, liveValue: e.target.value });
        });

        function onBlur() {
            success(input.value.trim());
        }
        input.addEventListener('blur', onBlur);

        input.addEventListener('keydown', function (e) {
            if (e.key === 'Enter') {
                success(input.value.trim());
            }
            if (e.key === 'Escape') {
                cancel();
            }
        });

        onRendered(function () {
            input.focus();
            input.style.height = '100%';
        });

        return input;
    };

    const columns = [
        { title: 'ID', field: 'id', visible: false },
        { title: '#', formatter: 'rownum', width: 60, headerSort: false },
        {
            title: 'Nazwa',
            field: 'name',
            editor: liveInputEditor,
            validator: ['required', 'minLength:3'],
            formatter: textWrapperFormatter,
        },
        {
            title: 'Email',
            field: 'email',
            editor: liveInputEditor,
            mutatorEdit: (value) => (value ? String(value).trim() : value),
            validator: [
                'required',
                {
                    type: strictEmailValidator,
                    parameters: {
                        message: 'Niepoprawny format adresu email',
                    },
                },
            ],
            formatter: textWrapperFormatter,
        },
        {
            title: gettext('Akcja'),
            resizable: false,
            headerSort: false,
            width: 100,
            formatter: RowContextMenuButtonColumn,
        },
    ];

    const rowContextMenu = (e, row) => {
        const table = row.getTable();
        const menu = [
            {
                label: gettext('Duplikuj'),
                action: (e, clickedRow) => {
                    const originalData = clickedRow.getData();
                    const duplicatedData = {
                        name: originalData.name
                            ? `${originalData.name} (Kopia)`
                            : '',
                        email: originalData.email
                            ? `${originalData.email} (Kopia)`
                            : '',
                    };
                    table.addRow(duplicatedData, false, clickedRow);
                    const newRow = table
                        .getRows()
                        .find((r) => r.getData() === duplicatedData);
                    if (newRow) newRow.getCell('email').edit(true);
                },
            },
            {
                label: gettext('Usuń'),
                action: async (e, rowToDelete) => {
                    const rowData = rowToDelete.getData();
                    if (!rowData.id) {
                        rowToDelete.delete();
                        addAlert(
                            'info',
                            gettext('Nowy wiersz został usunięty.'),
                        );
                        checkTableState();
                    } else {
                        const response = await sendDeleteRequest(
                            `${API_URL}${rowData.id}/`,
                        );
                        if (response.ok) {
                            let message = gettext('Pomyślnie usunięto.');
                            const data = await response.json();
                            if (data.message) message = data.message;
                            addAlert('success', message);
                            row.delete();
                        } else {
                            let message = gettext(
                                'Wystąpił błąd podczas usuwania.',
                            );
                            const data = await response.json();
                            if (data.message) message = data.message;
                            addAlert('error', message);
                        }
                    }
                },
            },
        ];
        return menu;
    };

    const table = initializeTable({
        tableElement: rootElement,
        filtersElement: document.querySelector(
            '#recipientsFilters .form-fields',
        ),
        ajaxURL: API_URL,
        columns: columns,
        rowContextMenu: rowContextMenu,
        pillsConfig: {
            containerSelector: pillsContainerSelector,
        },
        dataMode: 'local',
        progressive: false,
        validationMode: 'manual',
        height: '500px',
        layout: 'fitColumns',
    });

    table.on('cellEdited', () => checkTableState());
    table.on('rowAdded', (row) => {
        checkTableState();
        const rowData = row.getData();
        if (!rowData.name && !rowData.email) {
            setTimeout(() => row.getCell('name').edit(true), 0);
        }
    });
    document
        .getElementById('add-recipient-row-button')
        .addEventListener('click', () => table.addRow({}, true));

    saveButton.addEventListener('click', async () => {
        let isTableValid = true;
        const newRows = table.getRows().filter((row) => !row.getData().id);
        const editedCells = table.getEditedCells();
        const editedRowSet = new Set();
        editedCells.forEach((cell) => editedRowSet.add(cell.getRow()));
        const updatedRows = Array.from(editedRowSet).filter(
            (row) => row.getData().id,
        );
        const rowsToProcess = [...newRows, ...updatedRows];
        rowsToProcess.forEach((row) => {
            row.getCells().forEach((cell) => {
                if (cell.validate() !== true) {
                    isTableValid = false;
                    cell.getElement().classList.add(
                        'tabulator-validation-fail',
                    );
                }
            });
        });

        if (!isTableValid) {
            addAlert(
                'warning',
                gettext(
                    'Proszę poprawić błędy w formularzu (zaznaczone na czerwono).',
                ),
            );
            return;
        }

        const allRowsData = table.getData('active');
        const seenEmails = new Set();
        const duplicateEmails = new Set();

        allRowsData.forEach((data) => {
            const email = data.email ? data.email.toLowerCase() : '';
            if (!email) return;
            if (seenEmails.has(email)) {
                duplicateEmails.add(email);
            } else {
                seenEmails.add(email);
            }
        });

        if (duplicateEmails.size > 0) {
            table.getRows().forEach((row) => {
                const rowEmail = row.getData().email
                    ? row.getData().email.toLowerCase()
                    : '';
                if (duplicateEmails.has(rowEmail)) {
                    row.getCell('email')
                        .getElement()
                        .classList.add('tabulator-validation-fail');
                }
            });
            addAlert(
                'warning',
                gettext(
                    'Znaleziono zduplikowane adresy email. Proszę je poprawić.',
                ),
            );
            return;
        }

        const newRowsData = newRows.map((row) => row.getData());
        const updatedRowsData = updatedRows.map((row) => row.getData());

        const promises = [];

        newRowsData.forEach((rowData) => {
            promises.push(sendPostRequest(`${API_URL}`, rowData));
        });

        updatedRowsData.forEach((rowData) => {
            promises.push(
                sendPatchRequest(`${API_URL}${rowData.id}/`, rowData),
            );
        });

        if (promises.length === 0) {
            setButtonsEnabled(false);
            return;
        }

        try {
            const responses = await Promise.all(promises);

            for (const response of responses) {
                if (!response.ok) {
                    const errorData = await response.json();
                    throw { status: response.status, data: errorData };
                }
            }
            addAlert('success', 'Zmiany pomyślnie zapisane.');
            await table.setData();
            setButtonsEnabled(false);
        } catch (error) {
            addAlert('error', error);
        }
    });
    const filter_btn = document.querySelector('.filter-btn');
    if (filter_btn) {
        window.tables.initializeTableFilter();
        const filtersContainer = document.querySelector(
            '#recipientsFilters .form-fields',
        );
        if (
            filtersContainer &&
            window.utils &&
            typeof window.utils.hideOnClickOutside === 'function'
        ) {
            window.utils.hideOnClickOutside(filtersContainer, filter_btn);
        }
    }
    setButtonsEnabled(false);
}

export default initializeMailRecipientsTable;
